import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { FinancialOverviewPoint } from '../../services/apis/dashboard/dashboard.service';
import { ThemeService } from '../../services/theme.service';
import { chartColors, kpiColor } from './chart-theme';
import { FinancialOverviewMetric } from './financial-overview-chart.component';

interface OverviewDetailRow {
  month: string;
  earliestDate: string;
  kpiPercent: string;
  revenue: string;
  nfi: string;
  ebitda: string;
  netProfit: string;
  selected: boolean;
}

@Component({
  selector: 'app-overview-table-detail',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block h-full min-h-0 w-full' },
  templateUrl: './overview-table-detail.component.html',
})
export class OverviewTableDetailComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() data: FinancialOverviewPoint[] | null = null;
  @Input() drillData: FinancialOverviewPoint[] = [];
  @Input() metric: FinancialOverviewMetric = 'Revenue';
  @Input() period: 'current' | 'comparison' = 'current';
  @Input() selectedIndex = 0;
  @Output() selectedIndexChange = new EventEmitter<number>();

  options: EChartsOption | null = null;
  tableRows: OverviewDetailRow[] = [];
  clickTargets: Array<{ index: number; left: number; width: number; top: number; height: number; label: string }> = [];
  readonly viewportSize = 12;
  viewportStart = 0;

  private availableRows: FinancialOverviewPoint[] = [];
  private lastWheelTime = 0;

  get metricPercentHeader(): string {
    return `${this.metricDisplayLabel()} %`;
  }

  get hasScrollableViewport(): boolean {
    return this.availableRows.length > this.viewportSize;
  }

  get canMoveOlder(): boolean {
    return this.hasScrollableViewport && this.viewportStart > 0;
  }

  get canMoveNewer(): boolean {
    return this.hasScrollableViewport
      && this.viewportStart < this.availableRows.length - this.viewportSize;
  }

  get viewportLabel(): string {
    const rows = this.visibleRows();
    if (rows.length === 0) return '';
    const first = rows[0].periodLabel || rows[0].period;
    const last = rows[rows.length - 1].periodLabel || rows[rows.length - 1].period;
    return `${first} – ${last}`;
  }

  @ViewChild('chartContainer', { static: true }) private chartContainer?: ElementRef<HTMLElement>;

  private hasBuiltOptions = false;
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private chartInstance?: {
    resize: () => void;
    convertToPixel?: (finder: Record<string, unknown>, value: unknown) => number | number[];
    dispatchAction?: (payload: Record<string, unknown>) => void;
  };
  private readonly themeService = inject(ThemeService);
  private readonly ngZone = inject(NgZone);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) {
      this.buildView();
    }
    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildView());
  }

  ngOnChanges(): void {
    this.buildView();
  }

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) return;
    this.chartContainer.nativeElement.addEventListener('wheel', this.onNativeWheel, {
      passive: false,
      capture: true,
    });
    this.resizeObserver = new ResizeObserver(() => {
      this.chartInstance?.resize();
      queueMicrotask(() => this.updateClickTargets());
    });
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.chartContainer?.nativeElement.removeEventListener('wheel', this.onNativeWheel, true);
  }

  onChartInit(instance: {
    resize: () => void;
    convertToPixel?: (finder: Record<string, unknown>, value: unknown) => number | number[];
    dispatchAction?: (payload: Record<string, unknown>) => void;
  }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
    queueMicrotask(() => this.updateClickTargets());
  }

  onChartFinished(): void {
    this.updateClickTargets();
  }

  onChartClick(event: { dataIndex?: number }): void {
    if (typeof event?.dataIndex !== 'number') return;
    this.selectedIndex = event.dataIndex + this.viewportStart;
    this.buildView();
    this.selectedIndexChange.emit(this.selectedIndex);
  }

  moveViewport(direction: 'older' | 'newer'): void {
    if (!this.hasScrollableViewport) return;
    const maximumStart = Math.max(0, this.availableRows.length - this.viewportSize);
    const change = direction === 'newer' ? 1 : -1;
    const nextStart = Math.max(0, Math.min(maximumStart, this.viewportStart + change));
    if (nextStart === this.viewportStart) return;
    this.viewportStart = nextStart;
    this.buildView(false);
  }

  onWheel(event: WheelEvent): void {
    if (!this.hasScrollableViewport || Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;
    event.preventDefault();
    event.stopPropagation();
    const now = Date.now();
    if (now - this.lastWheelTime < 120) return;
    this.lastWheelTime = now;
    this.moveViewport(event.deltaY > 0 ? 'newer' : 'older');
  }

  private readonly onNativeWheel = (event: WheelEvent): void => {
    this.onWheel(event);
  };

  selectVisibleMonth(visibleIndex: number): void {
    if (visibleIndex < 0 || visibleIndex >= this.visibleRows().length) return;

    this.ngZone.run(() => {
      this.selectedIndex = visibleIndex + this.viewportStart;
      this.buildView();
      this.selectedIndexChange.emit(this.selectedIndex);
    });
  }

  showMonthTooltip(visibleIndex: number): void {
    this.chartInstance?.dispatchAction?.({ type: 'showTip', seriesIndex: 0, dataIndex: visibleIndex });
  }

  hideMonthTooltip(): void {
    this.chartInstance?.dispatchAction?.({ type: 'hideTip' });
  }

  private buildView(ensureSelectionVisible = true): void {
    this.hasBuiltOptions = true;
    const metricKey = this.metric === 'NFI' ? 'nfi' : this.metric;
    const apiRows = (this.drillData ?? [])
      .filter(row => row.kpiKey === metricKey)
      .sort((a, b) => a.period.localeCompare(b.period));
    this.availableRows = apiRows.length > 0 ? apiRows : (this.data ?? []);
    const selectedRowIndex = this.clampedSelectedIndex(this.availableRows);
    if (ensureSelectionVisible) this.ensureSelectedMonthIsVisible(selectedRowIndex);
    const selectedRows = selectedRowIndex >= 0 ? [this.availableRows[selectedRowIndex]] : [];
    this.tableRows = this.buildTableRows(selectedRows);
    const visibleRows = this.visibleRows();
    this.options = visibleRows.length > 0 ? this.buildOptions(visibleRows) : null;
    queueMicrotask(() => this.updateClickTargets());
  }

  private updateClickTargets(): void {
    const rows = this.visibleRows();
    const container = this.chartContainer?.nativeElement;
    if (!container || !this.chartInstance?.convertToPixel || rows.length === 0) {
      this.clickTargets = [];
      return;
    }

    const centers = rows
      .map((_, index) => {
        const pixel = this.chartInstance?.convertToPixel?.({ xAxisIndex: 0 }, index);
        return Array.isArray(pixel) ? pixel[0] : pixel;
      })
      .filter((value): value is number => Number.isFinite(value));
    if (centers.length !== rows.length) {
      this.clickTargets = [];
      return;
    }

    const fallbackHalfWidth = centers.length > 1 ? Math.abs(centers[1] - centers[0]) / 2 : 36;
    const top = this.hasScrollableViewport ? 58 : 36;
    const bottom = container.clientHeight - 48;
    this.clickTargets = centers.map((center, index) => {
      const left = index === 0 ? center - fallbackHalfWidth : (centers[index - 1] + center) / 2;
      const right = index === centers.length - 1 ? center + fallbackHalfWidth : (center + centers[index + 1]) / 2;
      return {
        index,
        left,
        width: Math.max(1, right - left),
        top,
        height: Math.max(1, bottom - top),
        label: rows[index].periodLabel || rows[index].period,
      };
    });
  }

  private buildOptions(rows: FinancialOverviewPoint[]): EChartsOption {
    const c = chartColors();
    const selected = this.selectedIndex - this.viewportStart;
    const labels = rows.map(row => row.periodLabel || row.period);
    const revenue = rows.map(row => Number(row.totalIncome ?? 0));
    const kpiPercent = rows.map(row => this.metricPercent(row, this.availableRows));
    const metricColor = this.metricColor();
    const selectedColor = document.documentElement.classList.contains('dark') ? '#52657A' : '#64748B';
    const selectedAccent = this.period === 'comparison'
      ? (document.documentElement.classList.contains('dark') ? '#A3E635' : '#65A30D')
      : (document.documentElement.classList.contains('dark') ? '#C28A3D' : '#92400E');

    return {
      animation: true,
      animationDuration: 180,
      grid: { left: 54, right: 54, top: this.hasScrollableViewport ? 58 : 36, bottom: 48, containLabel: true },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none',
          start: 0,
          end: 100,
          zoomOnMouseWheel: false,
          moveOnMouseWheel: false,
          moveOnMouseMove: false,
        },
      ],
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: { type: 'shadow', shadowStyle: { color: this.toRgba(c.primary || '#3B82F6', 0.08) } },
        backgroundColor: c.chartSurface,
        borderColor: c.cardBorder,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: { color: c.textPrimary, fontSize: 12 },
        extraCssText: 'box-shadow: 0 12px 28px rgba(0,0,0,0.18); border-radius: 8px;',
        formatter: (params: any) => this.tooltipFormatter(params, c),
      },
      legend: {
        bottom: 0,
        left: 4,
        itemGap: 18,
        icon: 'circle',
        data: [
          {
            name: 'Revenue',
            itemStyle: {
              color: selected >= 0 ? selectedColor : this.toRgba(c.primary || '#3B82F6', 0.76),
              borderColor: selected >= 0 ? selectedAccent : 'transparent',
              borderWidth: selected >= 0 ? 1 : 0,
            },
          },
          {
            name: `${this.metricLabel()} %`,
            itemStyle: { color: selected >= 0 ? selectedAccent : metricColor },
          },
        ],
        textStyle: { color: c.textSecondary, fontSize: 11, fontWeight: 600 },
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: c.cardBorder } },
        axisTick: { alignWithLabel: true, lineStyle: { color: c.cardBorder } },
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          margin: 10,
          // Every month gets a label - ECharts' auto interval was dropping alternate
          // ones, so the axis appeared to be missing months that the bars did have.
          interval: 0,
          hideOverlap: false,
          rotate: labels.length > 14 ? 45 : 0,
          formatter: (value: string, index: number) => index === selected ? `{selected|● ${value}}` : value,
          rich: {
            selected: { color: selectedAccent, fontSize: 11, fontWeight: 800 },
          },
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: { color: c.textSecondary, fontSize: 11, formatter: (value: number) => this.formatAxisCurrency(value) },
          splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed', opacity: 0.72 } },
        },
        {
          type: 'value',
          axisLabel: { color: c.textSecondary, fontSize: 11, formatter: (value: number) => `${value.toFixed(0)}%` },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: 'Revenue',
          type: 'bar',
          data: revenue.map((value, index) => ({
            value,
            itemStyle: {
              color: index === selected ? selectedColor : this.toRgba(c.primary || '#3B82F6', 0.76),
              borderColor: index === selected ? selectedAccent : 'transparent',
              borderWidth: index === selected ? 1 : 0,
              borderRadius: [7, 7, 4, 4],
            },
            label: {
              position: value < 0 ? 'insideBottom' : value === 0 ? 'top' : 'insideTop',
              distance: value === 0 ? 8 : 6,
              offset: value === 0 ? [-14, 0] : [0, 0],
              color: c.textPrimary,
            },
          })),
          barWidth: 24,
          barGap: '18%',
          label: {
            show: true,
            color: c.textPrimary,
            fontSize: 10,
            fontWeight: 800,
            formatter: (params: any) => this.formatCompactCurrency(Number(params.value ?? 0)),
          },
        },
        {
          name: `${this.metricLabel()} %`,
          type: 'line',
          yAxisIndex: 1,
          data: kpiPercent.map((value, index) => index === selected ? {
            value,
            symbolSize: 13,
            itemStyle: { color: selectedAccent, borderColor: c.chartSurface, borderWidth: 2 },
          } : value),
          smooth: 0.3,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: { color: metricColor },
          lineStyle: { color: metricColor, width: 2.4 },
          label: {
            show: true,
            position: 'top',
            distance: 10,
            offset: [14, 0],
            color: c.textPrimary,
            fontSize: 11,
            fontWeight: 850,
            formatter: (params: any) => `${Number(params.value ?? 0).toFixed(1)}%`,
          },
        },
      ],
    };
  }

  private buildTableRows(rows: FinancialOverviewPoint[]): OverviewDetailRow[] {
    return rows.map((row, index) => {
      const revenue = Number(row.totalIncome ?? 0);

      return {
        month: row.periodLabel || this.monthYearLabel(row.period) || this.monthNumber(row, index),
        earliestDate: this.earliestDate(row),
        kpiPercent: `${this.metricPercent(row, rows).toFixed(1)}%`,
        revenue: this.formatCurrency(revenue),
        nfi: this.formatCurrency(Number(row.nfi ?? 0)),
        ebitda: this.formatCurrency(Number(row.ebitda ?? 0)),
        netProfit: this.formatCurrency(Number(row.netProfit ?? 0)),
        selected: index === this.clampedSelectedIndex(rows),
      };
    });
  }

  private metricPercent(row: FinancialOverviewPoint, rows: FinancialOverviewPoint[]): number {
    if (row.kpiKey) return Number(row.kpiPercent ?? 0);
    if (this.metric === 'Revenue') {
      const revenue = Number(row.totalIncome ?? 0);
      const totalRevenue = rows.reduce((sum, item) => sum + Number(item.totalIncome ?? 0), 0);
      return totalRevenue ? Number(((revenue / totalRevenue) * 100).toFixed(2)) : 0;
    }

    if (this.metric === 'gross_profit_margin') return Number(row.grossProfitMargin ?? 0);
    if (this.metric === 'ebitda_margin') return Number(row.ebitdaMargin ?? 0);
    if (this.metric === 'net_profit_margin') return Number(row.netProfitMargin ?? 0);
    return 0;
  }

  private metricLabel(): string {
    if (this.metric === 'NFI') return 'NFI';
    if (this.metric === 'gross_profit_margin') return 'GPM';
    if (this.metric === 'ebitda_margin') return 'EBITDA';
    if (this.metric === 'net_profit_margin') return 'NPM';
    return 'Revenue';
  }

  private metricDisplayLabel(): string {
    if (this.metric === 'NFI') return 'NFI';
    if (this.metric === 'gross_profit_margin') return 'Gross Profit Margin';
    if (this.metric === 'ebitda_margin') return 'EBITDA Margin';
    if (this.metric === 'net_profit_margin') return 'Net Profit Margin';
    return 'Revenue';
  }

  private metricColor(): string {
    return this.metric === 'Revenue' ? (chartColors().primary || '#1AAE9F') : kpiColor(this.metric);
  }

  private monthNumber(row: FinancialOverviewPoint, index: number): string {
    if (row.monthNumber != null) return String(row.monthNumber);
    const date = this.parsePeriodDate(row.period);
    return String(date ? date.getMonth() + 1 : index + 1);
  }

  private monthYearLabel(period: string): string | null {
    const date = this.parsePeriodDate(period);
    return date?.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) ?? null;
  }

  private earliestDate(row: FinancialOverviewPoint): string {
    if (row.earliestDate) {
      const apiDate = new Date(row.earliestDate);
      if (!Number.isNaN(apiDate.getTime())) {
        return `${apiDate.getDate()}/${apiDate.getMonth() + 1}/${apiDate.getFullYear()}`;
      }
    }
    const date = this.parsePeriodDate(row.period);
    if (!date) return row.period;
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  }

  private parsePeriodDate(period: string): Date | null {
    const match = period.match(/^(\d{4})-(\d{2})/);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, 1);
  }

  private clampedSelectedIndex(rows: FinancialOverviewPoint[]): number {
    if (rows.length === 0) return -1;
    return Math.max(0, Math.min(this.selectedIndex, rows.length - 1));
  }

  private visibleRows(): FinancialOverviewPoint[] {
    return this.hasScrollableViewport
      ? this.availableRows.slice(this.viewportStart, this.viewportStart + this.viewportSize)
      : this.availableRows;
  }

  private ensureSelectedMonthIsVisible(selectedIndex: number): void {
    const maximumStart = Math.max(0, this.availableRows.length - this.viewportSize);
    this.viewportStart = Math.max(0, Math.min(maximumStart, this.viewportStart));
    if (!this.hasScrollableViewport || selectedIndex < 0) {
      this.viewportStart = 0;
      return;
    }
    if (selectedIndex < this.viewportStart) {
      this.viewportStart = selectedIndex;
    } else if (selectedIndex >= this.viewportStart + this.viewportSize) {
      this.viewportStart = selectedIndex - this.viewportSize + 1;
    }
  }

  private tooltipFormatter(params: any, c: ReturnType<typeof chartColors>): string {
    const rawItems = Array.isArray(params) ? params : [params];
    const items = rawItems.filter((item: any, index: number, all: any[]) =>
      all.findIndex(candidate => candidate.seriesName === item.seriesName) === index
    );
    const title = items[0]?.name ?? '';
    const rows = items.map((item: any) => {
      const isPercent = String(item.seriesName ?? '').includes('%');
      const value = isPercent
        ? `${Number(item.value ?? 0).toFixed(1)}%`
        : this.formatCurrency(Number(item.value ?? 0));
      return `<div style="display:flex;align-items:center;justify-content:space-between;gap:24px;margin-top:5px;">
        <span style="display:flex;align-items:center;gap:8px;color:${c.textSecondary};font-weight:650;">
          <span style="width:9px;height:9px;border-radius:999px;background:${item.color};display:inline-block;"></span>
          ${item.seriesName}
        </span>
        <strong style="color:${c.textPrimary};">${value}</strong>
      </div>`;
    }).join('');

    return `<div style="min-width:190px;font-family:Inter,system-ui,sans-serif;">
      <div style="color:${c.textPrimary};font-weight:850;margin-bottom:6px;">${title}</div>
      ${rows}
    </div>`;
  }

  private formatAxisCurrency(value: number): string {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
    if (abs >= 1_000) return `${sign}£${(abs / 1_000).toFixed(0)}K`;
    return `${sign}£${abs.toFixed(0)}`;
  }

  private formatCompactCurrency(value: number): string {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${sign}£${(abs / 1_000).toFixed(1)}K`;
    return `${sign}£${abs.toFixed(0)}`;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(value);
  }

  private toRgba(color: string, alpha: number): string {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color.trim());
    if (!match) return color;
    const r = parseInt(match[1], 16);
    const g = parseInt(match[2], 16);
    const b = parseInt(match[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
